/**
 * GuestDelmomentSection
 * Lists the work orders ("delmoment") linked to a project in the guest project
 * view. Reachable ones link to /guest/:token/wo/:key (open: interactive;
 * completed within 30 days: read-only). A cancelled delmoment, or one
 * completed longer ago, is not served by the guest API, so it is listed as a
 * plain row with its status rather than a link that would fail.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Badge } from 'react-bootstrap';
import { ListChecks, ChevronRight, CheckCircle } from 'lucide-react';
import { GuestWorkOrderSummary } from '../../../types/guestAccess';
import { WORK_ORDER_LABELS } from '../../WorkOrders/workOrderSections';
import { formatDateForStorage } from '../../../Helpers/DateUtils';

interface GuestDelmomentSectionProps {
    token: string;
    projectKey: string;
    workOrders: GuestWorkOrderSummary[];
}

/** The guest API serves archived work orders only while completed within this window. */
export const GUEST_ARCHIVE_WINDOW_DAYS = 30;

/**
 * True when the guest API will serve the delmoment: open, or completed within
 * the 30-day window. Same rule as the functions' `isRecentlyCompleted`: the
 * Stockholm timestamp is compared with the Stockholm-formatted cutoff, never
 * parsed in the browser's own timezone.
 */
export const isDelmomentReachable = (wo: GuestWorkOrderSummary, now: Date = new Date()): boolean => {
    if (!wo.archived) return true;
    if (wo.status !== 'completed' || !wo.completedAt) return false;
    const cutoff = formatDateForStorage(new Date(now.getTime() - GUEST_ARCHIVE_WINDOW_DAYS * 24 * 60 * 60 * 1000));
    return wo.completedAt >= cutoff;
};

/**
 * Glossary status label + bootstrap variant for a delmoment.
 * `GuestWorkOrderSummary.status` is 'open' | 'completed' | 'cancelled' (string
 * on the wire); anything else is shown as open rather than invented.
 */
export const workOrderStatusBadge = (status: string): { label: string; variant: string } => {
    switch (status) {
        case 'completed':
            return { label: WORK_ORDER_LABELS.statusCompleted, variant: 'success' };
        case 'cancelled':
            return { label: WORK_ORDER_LABELS.statusCancelled, variant: 'secondary' };
        default:
            return { label: WORK_ORDER_LABELS.statusOpen, variant: 'primary' };
    }
};

const GuestDelmomentSection: React.FC<GuestDelmomentSectionProps> = ({ token, projectKey, workOrders }) => {
    if (!workOrders || workOrders.length === 0) {
        return null;
    }

    // Open delmoment first (still need doing), then completed/cancelled.
    const sorted = [...workOrders].sort((a, b) => {
        const aOpen = a.status === 'open' ? 0 : 1;
        const bOpen = b.status === 'open' ? 0 : 1;
        return aOpen - bOpen;
    });

    return (
        <Card className="mb-4">
            <Card.Header className="d-flex align-items-center gap-2">
                <ListChecks size={18} />
                <span>{WORK_ORDER_LABELS.entityInProject}</span>
                <Badge bg="secondary">{workOrders.length}</Badge>
            </Card.Header>
            <Card.Body className="p-0">
                <div className="list-group list-group-flush">
                    {sorted.map((wo) => {
                        const badge = workOrderStatusBadge(wo.status);
                        const completed = wo.status === 'completed';
                        const content = (
                            <>
                                <span className="d-flex align-items-center gap-2 text-truncate">
                                    {completed && <CheckCircle size={16} className="text-success flex-shrink-0" />}
                                    <span className="text-truncate">{wo.title}</span>
                                </span>
                                <span className="d-flex align-items-center gap-2 flex-shrink-0">
                                    <Badge bg={badge.variant}>{badge.label}</Badge>
                                    {isDelmomentReachable(wo) && <ChevronRight size={18} className="text-muted" />}
                                </span>
                            </>
                        );
                        const className = 'list-group-item d-flex align-items-center justify-content-between gap-2';
                        return isDelmomentReachable(wo) ? (
                            <Link
                                key={wo.key}
                                to={`/guest/${token}/wo/${wo.key}`}
                                state={{ projectKey }}
                                className={`${className} list-group-item-action`}
                            >
                                {content}
                            </Link>
                        ) : (
                            <div key={wo.key} className={`${className} text-muted`}>
                                {content}
                            </div>
                        );
                    })}
                </div>
            </Card.Body>
        </Card>
    );
};

export default GuestDelmomentSection;
